import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, Eye, Download, Printer, FileText, Trash } from 'lucide-react';
import { DataStore, Report } from '@/lib/data-store';
import { ReportForm } from '@/components/ReportForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { generateReportCode, getNextReportNumber } from '@/lib/report-utils';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const LaporanKerja = () => {
  const data = DataStore.getData();
  const [reports, setReports] = useState<Report[]>(data.reports);
  const [projects] = useState(data.projects);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterJenis, setFilterJenis] = useState('');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleView = (report: Report) => {
    setSelectedReport(report);
    setShowDetail(true);
  };

  const handleSubmit = (data: Report) => {
    const newReports = editingId
      ? reports.map(r => r.id === editingId ? { ...data, id: editingId } : r)
      : [...reports, data];

    setReports(newReports);
    DataStore.saveData({ ...DataStore.getData(), reports: newReports });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = reports.filter(r => r.id !== id);
    setReports(filtered);
    DataStore.saveData({ ...DataStore.getData(), reports: filtered });
  };

  const filteredReports = useMemo(() => reports.filter(r => {
    const matchSearch = r.nomorLaporan.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       r.projek.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       r.oleh.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = !filterJenis || r.jenis === filterJenis;
    const matchProject = !selectedProject || r.projek === selectedProject;
    return matchSearch && matchFilter && matchProject;
  }), [reports, searchTerm, filterJenis, selectedProject]);

  const nextReportCode = useMemo(() => generateReportCode(getNextReportNumber(reports)), [reports]);

  // Get unique jenis values
  const jenisOptions = useMemo(() => Array.from(new Set(reports.map(r => r.jenis))), [reports]);

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="report" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">8. Laporan Kerja</h1>
          <p className="text-slate-600 mt-1">Dokumentasi laporan kerja dan progres projek</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {reports.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredReports, [
                    { label: 'Nombor Laporan', key: 'nomorLaporan' },
                    { label: 'Projek', key: 'projek' },
                    { label: 'Jenis', key: 'jenis' },
                    { label: 'Tarikh', key: 'tarikhMula' },
                    { label: 'Tarikh Akhir', key: 'tarikhAkhir' },
                    { label: 'Oleh', key: 'oleh' },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR LAPORAN KERJA', tableHTML, 'LAPORAN KERJA / WORK REPORT');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredReports, 'Laporan Kerja')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Laporan Baru</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Jumlah Laporan</p>
              <p className="text-3xl font-bold text-slate-900">{reports.length}</p>
              <p className="text-xs text-slate-500">total laporan kerja</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Laporan Selesai</p>
              <p className="text-3xl font-bold text-green-600">{reports.filter(r => r.status === 'Selesai' || r.status === 'Disetujui').length}</p>
              <p className="text-xs text-slate-500">laporan yang sudah selesai</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Laporan Draf</p>
              <p className="text-3xl font-bold text-yellow-600">{reports.filter(r => r.status === 'Draf').length}</p>
              <p className="text-xs text-slate-500">masih dalam draf</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <Input 
                placeholder="Cari laporan, projek, atau pelapor..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>
            <select 
              value={filterJenis}
              onChange={(e) => setFilterJenis(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Semua Jenis</option>
              {jenisOptions.map((jenis) => (
                <option key={jenis} value={jenis}>
                  {jenis}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Laporan Kerja</CardTitle>
          <CardDescription>Jumlah {filteredReports.length} laporan</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredReports.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data laporan. Sila tambahkan laporan baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="reports-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Nombor Laporan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Projek</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Jenis</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Oleh</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedProject(report.projek || '')}>
                      <td className="py-3 px-4 font-medium text-slate-900 flex items-center gap-2">
                        <FileText size={16} className="text-blue-500" />
                        {report.nomorLaporan}
                      </td>
                      <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline">{report.projek || '-'}</td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          {report.jenis}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{report.tarikhMula} s/d {report.tarikhAkhir}</td>
                      <td className="py-3 px-4 text-slate-600">{report.oleh}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          report.status === 'Disetujui' ? 'bg-green-100 text-green-700' :
                          report.status === 'Selesai' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button onClick={() => handleView(report)} className="text-purple-600 hover:text-purple-700 text-sm gap-1 flex items-center">
                          <Eye size={14} /> Lihat
                        </button>
                        <button onClick={() => handleEdit(report.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                          <Edit2 size={14} /> Sunting
                        </button>
                        <button onClick={() => handleDelete(report.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                          <Trash2 size={14} /> Padam
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Form Modal */}
      {showForm && (
        <ReportForm
          report={editingId ? reports.find(r => r.id === editingId) : undefined}
          nomorLaporan={editingId ? (reports.find(r => r.id === editingId)?.nomorLaporan || nextReportCode) : nextReportCode}
          projects={projects}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Report Detail Modal */}
      {showDetail && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
              <CardTitle>Laporan Kerja - {selectedReport.nomorLaporan}</CardTitle>
              <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-slate-100 rounded">
                <Plus size={20} style={{ transform: 'rotate(45deg)' }} />
              </button>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Nombor Laporan</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedReport.nomorLaporan}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Jenis</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedReport.jenis}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Projek</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedReport.projek || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Pelapor</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedReport.oleh}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tarikh</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedReport.tarikhMula} - {selectedReport.tarikhAkhir}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    selectedReport.status === 'Disetujui' ? 'bg-green-100 text-green-700' :
                    selectedReport.status === 'Selesai' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedReport.status}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-600 mb-2">Kandungan Laporan</p>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 whitespace-pre-wrap text-slate-700">
                  {selectedReport.kandungan}
                </div>
              </div>

              <div className="flex gap-3 pt-6 border-t">
                <Button className="bg-blue-600 hover:bg-blue-700 flex-1" onClick={() => handleEdit(selectedReport.id)}>
                  Sunting
                </Button>
                <Button variant="outline" onClick={() => setShowDetail(false)} className="flex-1">
                  Tutup
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LaporanKerja;